import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Download,
  UserPlus,
  MessageSquare,
  Upload,
  Ban,
  Pencil,
  Trash2,
  CheckCircle2,
  Users
} from 'lucide-react';
import contactService from '../../services/whatsapp/contactService';
import { useInboxStore } from '../../store/inboxStore';
import { Contact, OptInStatus } from '../../types/models/whatsapp';
import AddContactDialog from './contacts/AddContactDialog';
import ImportContactsDialog from './contacts/ImportContactsDialog';
import EditContactDialog from './contacts/EditContactDialog';
import DeleteContactDialog from './contacts/DeleteContactDialog';

const formatDate = (timestamp: number): string =>
  timestamp
    ? new Date(timestamp).toLocaleString([], {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : '—';

/**
 * 'unknown' means no consent was *recorded* here — it does not block sending,
 * which only an explicit opt-out does. Shown as a neutral dash rather than
 * "Unknown", which reads like a fault on every imported contact.
 */
const OPT_IN_LABEL: Record<OptInStatus, { text: string; tone: string; title: string }> = {
  opted_in: {
    text: 'Yes',
    tone: 'text-success-600',
    title: 'Consent recorded'
  },
  opted_out: {
    text: 'No',
    tone: 'text-danger-600',
    title: 'Opted out — outbound messages are blocked'
  },
  unknown: {
    text: '—',
    tone: 'text-gray-400',
    title: 'Not recorded. Sending still works; mark opted-in once you hold consent.'
  }
};

/** Escapes a CSV cell — quotes, commas and newlines all need wrapping. */
const csvCell = (value: string): string => {
  const text = String(value ?? '');
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/**
 * Contacts module — the directory behind the inbox.
 *
 * Mirrors the column set agents expect: name, number, created, opt-in state
 * and tags, with export, add, import and a direct path into a conversation.
 */
const WhatsAppContacts: React.FC = () => {
  const navigate = useNavigate();
  const selectConversation = useInboxStore((s) => s.selectConversation);

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [optInFilter, setOptInFilter] = useState<OptInStatus | 'all'>('all');
  const [editing, setEditing] = useState<Contact | null>(null);
  const [deleting, setDeleting] = useState<Contact | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [bulkOptingIn, setBulkOptingIn] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(
    () =>
      contactService.subscribe(
        (result) => {
          setContacts(result);
          setLoading(false);
        },
        (err) => {
          setError(err.message);
          setLoading(false);
        }
      ),
    []
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const digits = term.replace(/\D/g, '');

    return contacts.filter((contact) => {
      if (optInFilter !== 'all' && contact.optIn.status !== optInFilter) {
        return false;
      }
      if (!term) return true;
      return (
        contact.name.toLowerCase().includes(term) ||
        (digits.length > 0 && contact.phone.includes(digits))
      );
    });
  }, [contacts, search, optInFilter]);

  const handleExport = useCallback(() => {
    const header = ['Name', 'WhatsApp Number', 'Created At', 'Opted In', 'Tags'];
    const rows = filtered.map((c) => [
      csvCell(c.name),
      csvCell(c.phone),
      csvCell(formatDate(c.createdAt)),
      csvCell(
        c.optIn.status === 'unknown' ? 'Not recorded' : OPT_IN_LABEL[c.optIn.status].text
      ),
      csvCell(c.tags.join(' '))
    ]);

    const csv = [header, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `whatsapp-contacts-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [filtered]);

  const handleMessage = useCallback(
    async (contact: Contact) => {
      setBusyId(contact.id);
      setError(null);
      try {
        const { conversationId } = await contactService.startConversation(
          contact.phone,
          contact.name
        );
        selectConversation(conversationId);
        navigate('/whatsapp');
      } catch (err: any) {
        setError(err.message || 'Could not open this conversation');
      } finally {
        setBusyId(null);
      }
    },
    [navigate, selectConversation]
  );

  const handleToggleBlock = useCallback(async (contact: Contact) => {
    setBusyId(contact.id);
    setError(null);
    try {
      await contactService.setBlocked(contact.id, !contact.blocked);
    } catch (err: any) {
      setError(err.message || 'Could not update this contact');
    } finally {
      setBusyId(null);
    }
  }, []);

  // Selection is keyed on ids, not indexes: the list is a live subscription,
  // so rows can shift under the user while a selection is held.
  const visibleIds = useMemo(() => filtered.map((c) => c.id), [filtered]);

  const selectedVisible = useMemo(
    () => visibleIds.filter((id) => selected.has(id)),
    [visibleIds, selected]
  );

  const allVisibleSelected =
    visibleIds.length > 0 && selectedVisible.length === visibleIds.length;
  const someVisibleSelected =
    selectedVisible.length > 0 && !allVisibleSelected;

  const toggleOne = useCallback((contactId: string) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(contactId)) next.delete(contactId);
      else next.add(contactId);
      return next;
    });
  }, []);

  const toggleAllVisible = useCallback(() => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (visibleIds.every((id) => next.has(id))) {
        // Deselect only what is on screen, so a filtered-out selection
        // is not silently dropped.
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [visibleIds]);

  const handleBulkDelete = useCallback(async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;

    setBulkDeleting(true);
    setError(null);

    try {
      const result = await contactService.removeMany(ids);
      setSelected(new Set());

      if (result.skipped.length > 0) {
        const reasons = result.skipped
          .slice(0, 3)
          .map((row) => `${row.name || row.id}: ${row.reason}`)
          .join('; ');
        setError(
          `Deleted ${result.deleted}. ${result.skipped.length} skipped — ${reasons}${
            result.skipped.length > 3 ? '…' : ''
          }`
        );
      }
    } catch (err: any) {
      setError(err.message || 'Could not delete the selected contacts');
    } finally {
      setBulkDeleting(false);
      setConfirmBulk(false);
    }
  }, [selected]);

  const handleBulkOptIn = useCallback(async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;

    setBulkOptingIn(true);
    setError(null);

    try {
      const result = await contactService.bulkOptIn(ids);
      setSelected(new Set());

      if (result.skipped.length > 0) {
        const reasons = result.skipped
          .slice(0, 3)
          .map((row) => `${row.name || row.id}: ${row.reason}`)
          .join('; ');
        setError(
          `Marked ${result.updated} opted-in. ${result.skipped.length} skipped — ${reasons}${
            result.skipped.length > 3 ? '…' : ''
          }`
        );
      }
    } catch (err: any) {
      setError(err.message || 'Could not update the selected contacts');
    } finally {
      setBulkOptingIn(false);
    }
  }, [selected]);

  const handleDelete = useCallback(async (contactId: string) => {
    const result = await contactService.remove(contactId);
    // The list is a live Firestore subscription, so the row disappears on its
    // own — no refetch needed here.
    return result;
  }, []);

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col bg-wa-bg">
      <div className="flex flex-wrap items-center gap-2 border-b border-wa-border bg-wa-surface p-3">
        <h1 className="mr-2 flex items-center gap-2 text-base font-semibold text-wa-text">
          <Users className="h-5 w-5" />
          All Contacts
          <span className="rounded-full bg-wa-surface2 px-2 py-0.5 text-xs font-normal text-wa-muted">
            {filtered.length}
          </span>
        </h1>

        <div className="relative min-w-[16rem] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-wa-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search contacts by name or number"
            className="w-full rounded-lg border-wa-border bg-wa-surface2 pl-9 text-sm text-wa-text focus:border-wa-green focus:ring-wa-green"
          />
        </div>

        <select
          value={optInFilter}
          onChange={(e) => setOptInFilter(e.target.value as OptInStatus | 'all')}
          className="rounded-lg border-wa-border bg-wa-surface2 text-xs text-wa-text focus:border-wa-green focus:ring-wa-green"
        >
          <option value="all">All opt-in states</option>
          <option value="opted_in">Opted in</option>
          <option value="opted_out">Opted out</option>
          <option value="unknown">Unknown</option>
        </select>

        <button
          type="button"
          onClick={handleExport}
          disabled={filtered.length === 0}
          className="inline-flex items-center gap-1.5 rounded-lg border border-wa-border bg-wa-surface2 px-3 py-1.5 text-xs font-medium text-wa-text hover:bg-wa-surface disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" />
          Export
        </button>

        <button
          type="button"
          onClick={() => setImportOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-wa-border bg-wa-surface2 px-3 py-1.5 text-xs font-medium text-wa-text hover:bg-wa-surface"
        >
          <Upload className="h-3.5 w-3.5" />
          Import
        </button>

        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-wa-green px-3 py-1.5 text-xs font-medium text-white hover:bg-wa-teal"
        >
          <UserPlus className="h-3.5 w-3.5" />
          Add contact
        </button>
      </div>

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-wa-border bg-wa-surface2 px-4 py-2">
          <span className="text-xs font-medium text-wa-text">
            {selected.size} selected
          </span>

          <button
            type="button"
            onClick={() => {
              setSelected(new Set());
              setConfirmBulk(false);
            }}
            className="text-xs font-medium text-wa-muted underline hover:text-wa-text"
          >
            Clear
          </button>

          <button
            type="button"
            onClick={handleBulkOptIn}
            disabled={bulkOptingIn || confirmBulk}
            className="inline-flex items-center gap-1.5 rounded-lg border border-wa-border bg-wa-surface px-3 py-1.5 text-xs font-medium text-wa-text hover:bg-wa-surface2 disabled:opacity-50"
            title="Record consent you collected elsewhere. Opted-out contacts are never changed."
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            {bulkOptingIn ? 'Saving…' : 'Mark opted-in'}
          </button>

          {/* Two-step: deleting a large selection by a stray click is far
              worse than deleting one row, so the destructive action is never
              the first click. */}
          {confirmBulk ? (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-wa-text">
                Delete {selected.size} contact{selected.size === 1 ? '' : 's'}?
                Chat history is kept.
              </span>
              <button
                type="button"
                onClick={() => setConfirmBulk(false)}
                disabled={bulkDeleting}
                className="rounded-lg border border-wa-border px-3 py-1.5 text-xs font-medium text-wa-text hover:bg-wa-surface disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="inline-flex items-center gap-1.5 rounded-lg bg-danger-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-danger-500 disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
                {bulkDeleting ? 'Deleting…' : 'Yes, delete'}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmBulk(true)}
              className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-danger-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-danger-500"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete {selected.size} contact{selected.size === 1 ? '' : 's'}
            </button>
          )}
        </div>
      )}

      {error && (
        <div className="bg-danger-50 px-4 py-2 text-sm text-danger-600">
          {error}
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-2 font-medium underline"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto bg-wa-surface">
        <table className="w-full border-collapse text-[12px] whitespace-nowrap">
          <thead className="sticky top-0 z-10 bg-wa-surface2 shadow-sm">
            <tr className="text-left text-xs font-medium text-wa-muted">
              <th className="w-10 px-3 py-2.5">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  ref={(node) => {
                    // Indeterminate cannot be set in JSX — it is a property,
                    // not an attribute.
                    if (node) node.indeterminate = someVisibleSelected;
                  }}
                  onChange={toggleAllVisible}
                  className="rounded border-wa-border text-wa-green focus:ring-wa-green"
                  aria-label="Select all contacts"
                />
              </th>
              <th className="px-3 py-2.5">Sr No.</th>
              <th className="px-3 py-2.5">Name</th>
              <th className="px-3 py-2.5">WhatsApp Number</th>
              <th className="px-3 py-2.5">Created At</th>
              <th className="px-3 py-2.5">Opted In</th>
              <th className="px-3 py-2.5">Tags</th>
              <th className="px-3 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-wa-muted">
                  Loading contacts…
                </td>
              </tr>
            )}

            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-12 text-center">
                  <Users className="mx-auto h-10 w-10 text-wa-muted/50" />
                  <p className="mt-2 text-sm font-medium text-wa-muted">
                    {contacts.length === 0 ? 'No contacts yet' : 'No matches'}
                  </p>
                  <p className="mt-1 text-xs text-wa-muted/70">
                    {contacts.length === 0
                      ? 'Add a contact, import a CSV, or wait for an inbound message.'
                      : 'Try a different search or filter.'}
                  </p>
                </td>
              </tr>
            )}

            {!loading &&
              filtered.map((contact, index) => (
                <tr
                  key={contact.id}
                  className="h-9 border-b border-wa-border hover:bg-wa-surface2 transition-colors"
                >
                  <td className="w-10 px-3 py-1.5">
                    <input
                      type="checkbox"
                      checked={selected.has(contact.id)}
                      onChange={() => toggleOne(contact.id)}
                      className="rounded border-wa-border text-wa-green focus:ring-wa-green"
                      aria-label={`Select ${contact.name || contact.phone}`}
                    />
                  </td>
                  <td className="px-3 py-1.5 text-wa-muted">{index + 1}</td>
                  <td className="px-3 py-1.5 font-medium text-wa-text">
                    {contact.name || contact.phone}
                    {contact.blocked && (
                      <span className="ml-2 rounded bg-danger-50 px-1.5 py-0.5 text-[10px] text-danger-600">
                        Blocked
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-wa-text">
                    {contact.phone}
                  </td>
                  <td className="px-3 py-1.5 text-wa-muted">
                    {formatDate(contact.createdAt)}
                  </td>
                  <td
                    className={`px-3 py-1.5 font-medium ${
                      OPT_IN_LABEL[contact.optIn.status].tone
                    }`}
                    title={OPT_IN_LABEL[contact.optIn.status].title}
                  >
                    {OPT_IN_LABEL[contact.optIn.status].text}
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="flex flex-wrap gap-1">
                      {contact.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded bg-wa-surface2 px-1.5 py-0.5 text-[10px] text-wa-text"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        disabled={busyId === contact.id || contact.blocked}
                        onClick={() => handleMessage(contact)}
                        className="rounded p-1.5 text-wa-muted hover:bg-wa-surface2 hover:text-wa-text disabled:opacity-40"
                        title="Open conversation"
                        aria-label={`Message ${contact.name || contact.phone}`}
                      >
                        <MessageSquare className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        disabled={busyId === contact.id}
                        onClick={() => setEditing(contact)}
                        className="rounded p-1.5 text-wa-muted hover:bg-wa-surface2 hover:text-wa-text disabled:opacity-40"
                        title="Edit"
                        aria-label={`Edit ${contact.name || contact.phone}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        disabled={busyId === contact.id}
                        onClick={() => handleToggleBlock(contact)}
                        className={`rounded p-1.5 hover:bg-wa-surface2 disabled:opacity-40 ${
                          contact.blocked ? 'text-danger-500 hover:text-danger-400' : 'text-wa-muted hover:text-wa-text'
                        }`}
                        title={contact.blocked ? 'Unblock' : 'Block'}
                        aria-label={contact.blocked ? 'Unblock' : 'Block'}
                      >
                        <Ban className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        disabled={busyId === contact.id}
                        onClick={() => setDeleting(contact)}
                        className="rounded p-1.5 text-wa-muted hover:bg-wa-surface2 hover:text-danger-500 disabled:opacity-40"
                        title="Delete"
                        aria-label={`Delete ${contact.name || contact.phone}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <AddContactDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreate={(input) => contactService.create(input).then(() => {})}
      />

      <ImportContactsDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={(rows) => contactService.importRows(rows)}
      />

      <EditContactDialog
        contact={editing}
        onClose={() => setEditing(null)}
        onSave={(contactId, changes) =>
          contactService.update(contactId, changes)
        }
      />

      <DeleteContactDialog
        contact={deleting}
        onClose={() => setDeleting(null)}
        onDelete={handleDelete}
        onBlock={handleToggleBlock}
      />
    </div>
  );
};

export default WhatsAppContacts;
